import React, { useMemo, useState } from 'react';
import { Card, Row, Col, Typography, Tag, Modal, message } from 'antd';
import { CarOutlined, PlusOutlined } from '@ant-design/icons';
import type { AdmVehicle, Personnel, Company, ItModule } from '../../../types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { fetchSqlData, insertData, updateData, deleteData } from '../../../services/api';
import StandardTable, { createActionColumn } from '../../../components/common/StandardTable';
import FilterBar from '../../../components/common/FilterBar';
import AdmVehicleForm from '../../admin/vehicles/AdmVehicleForm';
import dayjs from 'dayjs';
import { useGetCompanies, useGetPersonnel } from '../../../hooks/genericHooks';
import Button from '../../../components/common/Button';

const { Title } = Typography;

const getStatusTag = (status: string | null | undefined, translateEnum: (value?: string | null) => string) => {
  if (!status) return null;
  let color: any = 'default';
  if (status === 'Available') color = 'success';
  else if (status === 'Assigned') color = 'processing';
  else if (status === 'InMaintenance') color = 'warning';
  else if (status === 'OutOfService') color = 'error';
  else if (status === 'Inactive') color = 'default';
  return <Tag color={color}>{translateEnum(status)}</Tag>;
};

const VEHICLE_ENUM_LABELS: Record<string, string> = {
  CompanyOwned: 'Company Owned',
  Rented: 'Rented',
  Leased: 'Leased',
  Available: 'Available',
  Assigned: 'Assigned',
  InMaintenance: 'In Maintenance',
  OutOfService: 'Out of Service',
  Inactive: 'Inactive',
  Gasoline: 'Gasoline',
  Diesel: 'Diesel',
  LPG: 'LPG',
  Electric: 'Electric',
  Hybrid: 'Hybrid',
};

const MecVehicleList: React.FC = () => {
  const locale = 'en-US';
  const translateEnum = (value?: string | null) => {
    if (!value) return '-';
    return VEHICLE_ENUM_LABELS[value] || String(value);
  };
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Data
  const { data: vehicles = [], isLoading: isLoadingVehicles, isError, error } = useQuery<AdmVehicle[], Error>({
    queryKey: ['machineryVehicles'],
    queryFn: () => fetchSqlData('adm_vehicles'),
    staleTime: 1000 * 60 * 5,
  });
  const { data: personnelResp, isLoading: isLoadingUsers } = useGetPersonnel();
  const usersData = useMemo<Personnel[]>(() => {
    const arr = Array.isArray(personnelResp) ? personnelResp : (personnelResp as any)?.data || [];
    return arr as Personnel[];
  }, [personnelResp]);
  const { data: departmentsData = [], isLoading: isLoadingDepartments } =
    useQuery<ItModule[], Error>({
      queryKey: ['departments'],
      queryFn: () => fetchSqlData('it_modules', undefined, undefined),
      staleTime: Infinity,
    });
  const { data: companiesHookData = [], isLoading: isLoadingCompanies } = useGetCompanies();
  const actualCompaniesArray = useMemo<Company[]>(() => {
    return Array.isArray(companiesHookData) ? companiesHookData : (companiesHookData?.data || []);
  }, [companiesHookData]);

  // State (filters)
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedOwnership, setSelectedOwnership] = useState<string | undefined>(undefined);
  const [selectedCompany, setSelectedCompany] = useState<number | undefined>(undefined);
  const [selectedUser, setSelectedUser] = useState<number | 'none' | undefined>(undefined);
  const [selectedStatus, setSelectedStatus] = useState<string | undefined>(undefined);
  const [selectedMachineType, setSelectedMachineType] = useState<string | undefined>(undefined);
  // Form drawer
  const [formOpen, setFormOpen] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState<AdmVehicle | null>(null);

  // Derived
  const usersMap = useMemo(() => {
    const map: { [key: number]: string } = {};
    usersData.forEach(user => {
      if (user.id) {
        map[user.id] = `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Unknown User';
      }
    });
    return map;
  }, [usersData]);

  const companiesMap = useMemo(() => {
    const map: Record<number, string> = {};
    actualCompaniesArray.forEach(company => {
      if (company.id) {
        map[company.id] = company.name || '-';
      }
    });
    return map;
  }, [actualCompaniesArray]);

  const companyOptions = useMemo(() => {
    const companyIds = new Set(
      vehicles.map(vehicle => vehicle.company_id).filter((id): id is number => id != null)
    );
    return actualCompaniesArray
      .filter(company => company.id != null && companyIds.has(company.id))
      .map(company => ({
        value: company.id,
        label: company.name || '-',
      }))
      .sort((a, b) => a.label.localeCompare(b.label, locale));
  }, [actualCompaniesArray, vehicles, locale]);

  const machineTypeOptions = useMemo(() => {
    const types = new Set<string>();
    vehicles.forEach(vehicle => {
      if (vehicle.machine_type && typeof vehicle.machine_type === 'string' && vehicle.machine_type.trim() !== '') {
        types.add(vehicle.machine_type.trim());
      }
    });
    return Array.from(types).sort().map(type => ({ value: type, label: type }));
  }, [vehicles]);

  const vehicleUserIds = useMemo(() =>
    Array.from(new Set(vehicles.map(v => v.operator_id).filter(Boolean)))
  , [vehicles]);
  const vehicleUsers = usersData.filter(user => vehicleUserIds.includes(user.id));

  const filteredVehicles = useMemo(() => {
    let filtered = vehicles;
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      filtered = filtered.filter(vehicle =>
        (vehicle.license_plate?.toLowerCase().includes(s)) ||
        (vehicle.make?.toLowerCase().includes(s)) ||
        (vehicle.model?.toLowerCase().includes(s)) ||
        (vehicle.year?.toString().includes(s)) ||
        (vehicle.fuel_type?.toLowerCase().includes(s))
      );
    }
    if (selectedOwnership !== undefined) {
      filtered = filtered.filter(vehicle => vehicle.ownership_type === selectedOwnership);
    }
    if (selectedCompany !== undefined) {
      filtered = filtered.filter(vehicle => vehicle.company_id === selectedCompany);
    }
    if (selectedUser !== undefined) {
      if (selectedUser === 'none') {
        filtered = filtered.filter(vehicle => vehicle.operator_id == null);
      } else {
        filtered = filtered.filter(vehicle => vehicle.operator_id === selectedUser);
      }
    }
    if (selectedStatus !== undefined) {
      filtered = filtered.filter(vehicle => vehicle.status === selectedStatus);
    }
    if (selectedMachineType !== undefined) {
      filtered = filtered.filter(vehicle => vehicle.machine_type === selectedMachineType);
    }
    return filtered;
  }, [vehicles, searchTerm, selectedOwnership, selectedCompany, selectedUser, selectedStatus, selectedMachineType]);

  // Handlers
  const handleAdd = () => {
    setEditingVehicle(null);
    setFormOpen(true);
  };
  const handleEdit = (record: AdmVehicle) => {
    setEditingVehicle(record);
    setFormOpen(true);
  };
  const handleCloseForm = () => {
    setFormOpen(false);
    setEditingVehicle(null);
  };
  const handleDelete = (id: number) => {
    Modal.confirm({
      title: 'Are you sure you want to delete this vehicle?',
      okText: 'Yes',
      cancelText: 'No',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await deleteData('adm_vehicles', 'id', id);
          message.success('Vehicle deleted');
          queryClient.invalidateQueries({ queryKey: ['machineryVehicles'] });
        } catch (e: any) {
          const errorMsg = e?.response?.data?.error || e.message || 'Delete failed';
          message.error(errorMsg);
        }
      }
    });
  };
  const handleSubmit = async (values: any) => {
    const dataToSubmit: Partial<AdmVehicle> = {
      ...values,
      year: values.year ? (isNaN(parseInt(values.year, 10)) ? null : parseInt(values.year, 10)) : null,
      kilometer: values.kilometer ? (isNaN(parseInt(values.kilometer, 10)) ? null : parseInt(values.kilometer, 10)) : null,
      operator_id: values.current_user_id ? (isNaN(parseInt(values.current_user_id, 10)) ? null : parseInt(values.current_user_id, 10)) : null,
      company_id: values.company_id ? (isNaN(parseInt(values.company_id, 10)) ? null : parseInt(values.company_id, 10)) : null,
      license_plate: values.license_plate?.trim() || '',
      ownership_type: values.ownership_type || 'CompanyOwned',
      status: values.status || 'Available',
      make: values.make?.trim() || undefined,
      model: values.model?.trim() || undefined,
      fuel_type: values.fuel_type || undefined,
      machine_type: values.machine_type || undefined,
      hours_counter: values.hours_counter ? (isNaN(parseInt(values.hours_counter, 10)) ? null : parseInt(values.hours_counter, 10)) : null,
      passenger_capacity: values.passenger_capacity ? (isNaN(parseInt(values.passenger_capacity, 10)) ? null : parseInt(values.passenger_capacity, 10)) : null,
      contract_reference_id: values.contract_reference_id?.trim() || null,
    };
    if ('current_user_id' in dataToSubmit) {
      delete (dataToSubmit as any).current_user_id;
    }
    if (editingVehicle && editingVehicle.id) {
      const numericId = parseInt(String(editingVehicle.id), 10);
      if (!isNaN(numericId)) {
        try {
          await updateData('adm_vehicles', numericId, dataToSubmit);
          message.success('Vehicle updated');
          queryClient.invalidateQueries({ queryKey: ['machineryVehicles'] });
          handleCloseForm();
        } catch (e: any) {
          const errorMsg = e?.response?.data?.error || e.message || 'Update failed';
          message.error(errorMsg);
        }
      }
    } else {
      try {
        await insertData('adm_vehicles', dataToSubmit);
        message.success('Vehicle added');
        queryClient.invalidateQueries({ queryKey: ['machineryVehicles'] });
        handleCloseForm();
      } catch (e: any) {
        const errorMsg = e?.response?.data?.error || e.message || 'Record could not be added';
        if (errorMsg.includes('duplicate key') && errorMsg.includes('license_plate')) {
          message.error('A vehicle with this plate already exists');
        } else {
          message.error(errorMsg);
        }
      }
    }
  };

  // Columns
  const columns: any[] = [
    { title: 'License Plate', dataIndex: 'license_plate', key: 'license_plate',
      width: 100,
      sorter: (a: AdmVehicle, b: AdmVehicle) => (a.license_plate || '').localeCompare(b.license_plate || ''),
      exportValue: (r: AdmVehicle) => r.license_plate || '-' },
    { title: 'Make', dataIndex: 'make', key: 'make', render: (text: any) => text || '-',
      width: 100,
      sorter: (a: AdmVehicle, b: AdmVehicle) => (a.make || '').localeCompare(b.make || ''),
      exportValue: (r: AdmVehicle) => r.make || '-' },
    { title: 'Model', dataIndex: 'model', key: 'model', render: (text: any) => text || '-',
      width: 100,
      sorter: (a: AdmVehicle, b: AdmVehicle) => (a.model || '').localeCompare(b.model || ''),
      exportValue: (r: AdmVehicle) => r.model || '-' },
    { title: 'Year', dataIndex: 'year', key: 'year', render: (text: any) => text || '-',
      sorter: (a: AdmVehicle, b: AdmVehicle) => (a.year || 0) - (b.year || 0), width: 70, align: 'center',
      exportValue: (r: AdmVehicle) => (r.year ?? '') },
    { title: 'Arrival Date', dataIndex: 'arrival_date', key: 'arrival_date', width: 100,
      render: (value: string) => (value ? dayjs(value).format('DD/MM/YYYY') : '-'),
      sorter: (a: AdmVehicle, b: AdmVehicle) => (a.arrival_date || '').localeCompare(b.arrival_date || ''),
      exportValue: (r: AdmVehicle) => (r.arrival_date ? dayjs(r.arrival_date).format('DD/MM/YYYY') : '-') },
    { title: 'Departure Date', dataIndex: 'departure_date', key: 'departure_date', width: 110,
      render: (value: string) => (value ? dayjs(value).format('DD/MM/YYYY') : '-'),
      sorter: (a: AdmVehicle, b: AdmVehicle) => (a.departure_date || '').localeCompare(b.departure_date || ''),
      exportValue: (r: AdmVehicle) => (r.departure_date ? dayjs(r.departure_date).format('DD/MM/YYYY') : '-') },
    { title: 'Km', dataIndex: 'kilometer', key: 'kilometer',
      render: (text: any) => text?.toLocaleString(locale) || '-',
      width: 70,
      sorter: (a: AdmVehicle, b: AdmVehicle) => (a.kilometer || 0) - (b.kilometer || 0), align: 'right',
      exportValue: (r: AdmVehicle) => (r.kilometer ?? '') },
    { title: 'Company', dataIndex: 'company_id', key: 'company_id',
      width: 120,
      render: (value: any) => companiesMap[value as number] || '-',
      sorter: (a: AdmVehicle, b: AdmVehicle) => (companiesMap[a.company_id as number] || '').localeCompare(companiesMap[b.company_id as number] || ''),
      exportValue: (r: AdmVehicle) => (companiesMap[r.company_id as number] || '-') },
    { title: 'Contract', dataIndex: 'contract_reference_id', key: 'contract_reference_id', width: 110,
      render: (value?: string | null) => value || '-',
      sorter: (a: AdmVehicle, b: AdmVehicle) => (a.contract_reference_id || '').localeCompare(b.contract_reference_id || ''),
      exportValue: (r: AdmVehicle) => r.contract_reference_id || '-' },
    { title: 'Type', dataIndex: 'machine_type', key: 'machine_type', render: (text: any) => text || '-',
      sorter: (a: AdmVehicle, b: AdmVehicle) => (a.machine_type || '').localeCompare(b.machine_type || ''), width: 140,
      exportValue: (r: AdmVehicle) => r.machine_type || '-' },
    { title: 'Fuel', dataIndex: 'fuel_type', key: 'fuel_type',
      width: 70,
      render: (text: any) => translateEnum(text),
      sorter: (a: AdmVehicle, b: AdmVehicle) => (a.fuel_type || '').localeCompare(b.fuel_type || ''),
      exportValue: (r: AdmVehicle) => translateEnum(r.fuel_type as any) },
    { title: 'Ownership', dataIndex: 'ownership_type', key: 'ownership_type',
      width: 120,
      render: (text: any) => translateEnum(text),
      sorter: (a: AdmVehicle, b: AdmVehicle) => (a.ownership_type || '').localeCompare(b.ownership_type || ''),
      exportValue: (r: AdmVehicle) => translateEnum(r.ownership_type as any) },
    { title: 'User', dataIndex: 'operator_id', key: 'operator_id',
      width: 170,
      render: (id: any) => usersMap[id as number] || '-',
      sorter: (a: AdmVehicle, b: AdmVehicle) => (usersMap[a.operator_id as number] || '').localeCompare(usersMap[b.operator_id as number] || ''),
      exportValue: (r: AdmVehicle) => (usersMap[r.operator_id as number] || '-') },
    { title: 'Status', dataIndex: 'status', key: 'status',
      render: (text: any) => getStatusTag(text, translateEnum),
      sorter: (a: AdmVehicle, b: AdmVehicle) => (a.status || '').localeCompare(b.status || ''), width: 130, align: 'center',
      exportValue: (r: AdmVehicle) => translateEnum(r.status as any) },
    createActionColumn<AdmVehicle>({
      onView: (record) => record.id && navigate(`/machinery/vehicles/show/${record.id}`),
      onEdit: (record) => handleEdit(record),
      onDelete: (record) => {
        const numericId = record.id ? parseInt(String(record.id), 10) : NaN;
        if (!isNaN(numericId)) handleDelete(numericId);
      },
      showView: true,
      showEdit: true,
      showDelete: true,
      align: 'left',
      width: 110,
    }) as any,
  ];

  if (isError) {
    message.error(`Vehicles could not be loaded: ${error?.message || 'Unknown error'}`);
  }

  return (
    <Card>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>
            <CarOutlined style={{ marginRight: 8 }} />
            {'Vehicles'}
          </Title>
        </Col>
        <Col>
          <Button action="add" type="primary" icon={<PlusOutlined />} onClick={handleAdd} loading={isLoadingDepartments || isLoadingUsers || isLoadingCompanies}>
            {'Add Vehicle'}
          </Button>
        </Col>
      </Row>

      <FilterBar
        gutter={6}
        items={[
          { type: 'text', key: 'search', placeholder: 'Search plate, make, model, year, or fuel...', value: searchTerm, onChange: v => setSearchTerm(v), width: 220 },
          { type: 'select', key: 'ownership', placeholder: 'Ownership', value: selectedOwnership as any, onChange: v => setSelectedOwnership(v), options: [
            { value: 'CompanyOwned', label: 'Company Owned' },
            { value: 'Rented', label: 'Rented' },
            { value: 'Leased', label: 'Leased' },
          ], width: 150, allowClear: true },
          { type: 'select', key: 'company', placeholder: 'Company', value: selectedCompany as any, onChange: v => setSelectedCompany(v), options: companyOptions, width: 180, allowClear: true, showSearch: true },
          { type: 'select', key: 'user', placeholder: 'User', value: selectedUser as any, onChange: v => setSelectedUser(v), options: [
            { value: 'none', label: 'Unassigned' },
            ...vehicleUsers.map(user => ({ value: user.id, label: `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Unknown User' }))
          ], width: 180, allowClear: true, showSearch: true },
          { type: 'select', key: 'type', placeholder: 'Vehicle type', value: selectedMachineType as any, onChange: v => setSelectedMachineType(v), options: machineTypeOptions, width: 160, allowClear: true, showSearch: true },
          { type: 'select', key: 'status', placeholder: 'Status', value: selectedStatus as any, onChange: v => setSelectedStatus(v), options: [
            { value: 'Available', label: 'Available' },
            { value: 'Assigned', label: 'Assigned' },
            { value: 'InMaintenance', label: 'In Maintenance' },
            { value: 'OutOfService', label: 'Out of Service' },
            { value: 'Inactive', label: 'Inactive' },
          ], width: 160, allowClear: true },
        ]}
        onReset={() => {
          setSearchTerm('');
          setSelectedOwnership(undefined);
          setSelectedCompany(undefined);
          setSelectedUser(undefined);
          setSelectedStatus(undefined);
          setSelectedMachineType(undefined);
        }}
      />

      <StandardTable
        rowKey="id"
        columns={columns}
        dataSource={filteredVehicles}
        size="small"
        scroll={{ x: 'max-content' }}
        pagination={{ pageSize: 20, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
        loading={isLoadingVehicles || isLoadingUsers}
        bordered
        enableCellTooltips
      />

      <AdmVehicleForm
        open={formOpen}
        onClose={handleCloseForm}
        initialValues={editingVehicle}
        onSubmit={handleSubmit}
        companies={actualCompaniesArray}
        departments={departmentsData}
        personnel={usersData}
        isLoadingLookups={isLoadingDepartments || isLoadingUsers || isLoadingCompanies}
      />
    </Card>
  );
};

export default MecVehicleList;
